import React, { useEffect } from 'react'
import { Toaster } from 'react-hot-toast'
import { getTheme, type ThemeMode } from '../../theme'
import { setThemeMode } from '../../lib/notifications'

interface NotificationProviderProps {
  children: React.ReactNode
  theme?: 'light' | 'dark' | 'system'
  position?:
    | 'top-left'
    | 'top-center'
    | 'top-right'
    | 'bottom-left'
    | 'bottom-center'
    | 'bottom-right'
}

/**
 * Notification provider component that wraps the Toaster
 * This should be placed at the root of your app
 * Automatically adapts to theme changes
 */
export const NotificationProvider = ({
  children,
  theme = 'light',
  position = 'bottom-center',
}: NotificationProviderProps) => {
  // Resolve theme mode (handle 'system' if needed)
  const themeMode: ThemeMode = theme === 'system' ? 'light' : theme
  
  // Get theme values
  const themeValues = getTheme(themeMode)
  
  // Update notification system with current theme
  useEffect(() => {
    setThemeMode(themeMode)
  }, [themeMode])
  
  return (
    <>
      {children}
      <Toaster
        position={position}
        reverseOrder={false}
        gutter={8}
        containerStyle={{}}
        containerClassName=""
        toastOptions={{
          duration: 3000,
          style: {
            background: themeValues.surface.elevated,
            color: themeValues.text.primary,
            borderRadius: '8px',
            padding: '12px 16px',
            fontSize: '14px',
            fontWeight: '500',
            boxShadow: themeValues.modal.shadow,
            border: `1px solid ${themeValues.border.primary}`,
          },
          success: {
            iconTheme: {
              primary: themeValues.status.success.icon,
              secondary: themeValues.status.success.bg,
            },
          },
          error: {
            iconTheme: {
              primary: themeValues.status.error.icon,
              secondary: themeValues.status.error.bg,
            },
          },
          loading: {
            iconTheme: {
              primary: themeValues.status.info.icon,
              secondary: themeValues.status.info.bg,
            },
          },
        }}
      />
    </>
  )
}

export default NotificationProvider
